// @flow

const focusableElementSelector =
  'input, select, textarea, a[href], button, [tabindex], audio[controls], video[controls], [contenteditable]:not([contenteditable=false])';

const isHTMLElement = (element: Element) => {
  return element instanceof HTMLElement;
};

const isDisabled = (element: HTMLElement) => {
  return Boolean(((element: any): HTMLInputElement).disabled);
};

const hasTabIndex = (element: Element) => {
  return element.hasAttribute('tabindex');
};

const isHidden = (element: HTMLElement) => {
  if (
    element.parentElement &&
    isHidden(((element.parentElement: any): HTMLElement))
  )
    return true;
  return element.hidden;
};

const isContentEditable = (element: Element) => {
  const value = element.getAttribute('contenteditable');
  return value !== 'false' && value != null;
};

export const isFocusable = (element: Element) => {
  if (!isHTMLElement(element)) return false;

  // This is just to make flow happy and is basically a no-op
  const htmlElement = ((element: any): HTMLElement);

  if (isHidden(htmlElement)) return false;
  if (isDisabled(htmlElement)) return false;

  const { localName } = element;
  const focusableTags = ['input', 'select', 'textarea', 'button'];

  // Make sure the tag is focusable
  if (focusableTags.indexOf(localName) >= 0) return true;

  // These elements have to have special attributes to be focusable
  const others = {
    a: () => element.hasAttribute('href'),
    audio: () => element.hasAttribute('controls'),
    video: () => element.hasAttribute('controls'),
  };

  if (localName in others) {
    return others[localName]();
  }

  if (isContentEditable(element)) return true;

  return hasTabIndex(element);
};

/**
 * Returns an array of all focusable elements within a container
 */
const getAllFocusableIn = (container: Element) => {
  const allFocusable = Array.from(
    container.querySelectorAll(focusableElementSelector)
  );
  return allFocusable.filter(isFocusable);
};

/**
 * Finds the first focusable element inside of an element
 */
export const getFirstFocusableIn = (container: Element) => {
  const allFocusable = getAllFocusableIn(container);
  return allFocusable.length ? allFocusable[0] : null;
};
